import { GetProductReviewsQuery } from "@omni/types/api/reviews/bff/request/productReviews.query";
import { Review } from "@omni/types/api/reviews/bff/response/review.model";
import { ReviewResponse } from "@omni/types/api/reviews/bff/response/reviews.response";
import { ReviewsSummary } from "@omni/types/api/reviews/bff/response/reviewsSummary.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { Locale } from "@omni/types/enums/locale.enum";
import { isDefined } from "@omni/utils/helper.utils";
import qs from "query-string";
import { createContext, use, useRef, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ReviewsMapperBFF } from "@/api/mappers/review.mapper";
import { useFeatureToggle } from "@/context/featureToggles.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { ReviewsSearchStort as ReviewsSearchSort } from "@/types/service/reviews/reviews.api";

import { PAGE_SIZE, ReviewsSearchSortToSortStringMap } from "../reviews.config";

type ReviewsContextValue = {
    sku: string;
    brand?: string;
    category?: string;
    range: number;
    reviewsEnabled: boolean;
    reviewsSummary: ReviewsSummary | undefined;
    reviews: Array<Review>;
    totalReviews: number;
    loading: boolean;
    sort: ReviewsSearchSort;
    locales: Array<Locale>;
    ratings: Array<string>;
    loadMore: () => Promise<void>;
    setSort: (sort: ReviewsSearchSort) => Promise<void>;
    toggleLocale: (locale: Locale) => Promise<void>;
    toggleRating: (rating: string) => Promise<void>;
    clearFilters: () => Promise<void>;
};

type ReviewsContextProviderProps = {
    sku: string;
    brand?: string;
    category?: string;
    summary?: ReviewsSummary;
    initialTotalReviews?: number;
    initialReviews?: Array<Review>;
};

export const ReviewsContext = createContext<ReviewsContextValue | undefined>(
    undefined
);
ReviewsContext.displayName = "ReviewsContext";

export const ReviewsContextProvider = ({
    sku,
    brand,
    category,
    summary,
    initialTotalReviews,
    initialReviews,
    children
}: RequireChildrenFor<ReviewsContextProviderProps>) => {
    const { t } = useGranite();
    const reviewsEnabled = useFeatureToggle(FeatureToggleKey.FEATURE_REVIEWS);

    const [page, setPageValue] = useState<number>(0);

    const [ratings, setRatingsValue] = useState<Array<string>>([]);

    const [locales, setLocalesValue] = useState<Array<Locale>>([]);

    const [sort, setSortValue] = useState<ReviewsSearchSort>(
        ReviewsSearchSort.MOST_RELEVANT
    );

    const [reviews, setReviews] = useState<Array<Review>>(initialReviews ?? []);

    const [totalReviews, setTotalReviews] = useState<number>(
        initialTotalReviews ?? 0
    );

    const abortControllerRef = useRef<AbortController | null>(null);

    const [{ loading }, fetchReviews] = useBFFProductServiceApi<
        undefined,
        ReviewResponse,
        ReviewResponse
    >(
        {
            url: "/reviews/product",
            method: "GET",
            mapper: (model) => ReviewsMapperBFF(model)(t)
        },
        {
            manual: true,
            useCache: false
        }
    );

    const getReviews = async (
        partialRequest: Partial<GetProductReviewsQuery>
    ) => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        const request: GetProductReviewsQuery = {
            sku: sku,
            offset: 0,
            limit: PAGE_SIZE,
            sort: ReviewsSearchSortToSortStringMap[sort],
            contentLocale: locales ?? [],
            rating: ratings ?? [],
            ...partialRequest
        };

        try {
            const response = await fetchReviews({
                signal: abortControllerRef.current.signal,
                params: request,
                paramsSerializer: {
                    serialize: (params) =>
                        qs.stringify(params, { arrayFormat: "comma" })
                }
            });

            return {
                reviews: response.data.reviews,
                totalReviews: response.data.totalResults
            };
        } catch {
            return null;
        }
    };

    const loadMore = async () => {
        const newPage = page + 1;

        setPageValue(newPage);

        const reviewsResponse = await getReviews({
            offset: newPage * PAGE_SIZE
        });
        if (!isDefined(reviewsResponse)) return;

        const { reviews, totalReviews } = reviewsResponse;

        setReviews((prev) => [...prev, ...reviews]);
        setTotalReviews(totalReviews);
    };

    const setSort = async (newSort: ReviewsSearchSort) => {
        setPageValue(0);
        setSortValue(newSort);

        const reviewsResponse = await getReviews({
            sort: ReviewsSearchSortToSortStringMap[newSort]
        });
        if (!isDefined(reviewsResponse)) return;

        const { reviews, totalReviews } = reviewsResponse;

        setReviews(reviews);
        setTotalReviews(totalReviews);
    };

    const toggleLocale = async (locale: Locale) => {
        const newLocales = locales.includes(locale)
            ? locales.filter((l) => l !== locale)
            : [...locales, locale];

        setPageValue(0);
        setLocalesValue(newLocales);

        const reviewsResponse = await getReviews({
            contentLocale: newLocales
        });
        if (!isDefined(reviewsResponse)) return;

        const { reviews, totalReviews } = reviewsResponse;

        setReviews(reviews);
        setTotalReviews(totalReviews);
    };

    const toggleRating = async (rating: string) => {
        const newRatings = ratings.includes(rating)
            ? ratings.filter((r) => r !== rating)
            : [...ratings, rating];

        setPageValue(0);
        setRatingsValue(newRatings);

        const reviewsResponse = await getReviews({
            rating: newRatings
        });
        if (!isDefined(reviewsResponse)) return;

        const { reviews, totalReviews } = reviewsResponse;

        setReviews(reviews);
        setTotalReviews(totalReviews);
    };

    const clearFilters = async () => {
        setRatingsValue([]);
        setLocalesValue([]);

        const reviewsResponse = await getReviews({
            contentLocale: [],
            rating: []
        });
        if (!isDefined(reviewsResponse)) return;

        const { reviews, totalReviews } = reviewsResponse;

        setReviews(reviews);
        setTotalReviews(totalReviews);
    };

    return (
        <ReviewsContext
            value={{
                sku: sku,
                brand: brand,
                category: category,
                range: summary?.range ?? 5,
                reviewsEnabled: reviewsEnabled,
                reviewsSummary: summary,
                reviews: reviews,
                totalReviews: totalReviews,
                loading: loading,
                sort: sort,
                locales: locales,
                ratings: ratings,
                loadMore: loadMore,
                setSort: setSort,
                toggleLocale: toggleLocale,
                toggleRating: toggleRating,
                clearFilters: clearFilters
            }}
        >
            {children}
        </ReviewsContext>
    );
};

export const useReviewsContext = () => {
    const context = use<ReviewsContextValue | undefined>(ReviewsContext);

    if (!isDefined(context)) {
        throw new Error(`${ReviewsContext.displayName} is undefined.`);
    }

    return context;
};
